import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { ButtonTypes } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ErrorCard } from '@eika/card';
import { IconTypes } from '@eika/icon';

import { TransferResult } from '@/components/transfer/components';
import { AccountService } from '@/domain/account';
import { useTransferToAccountMutation } from '@/domain/pay';
import useText from '@/hooks/use-text';
import piwik from '@/piwik/piwik';

import { useSession } from '@components/app';
import BankIdModal from '@components/transfer/bankid/BankIdModal';
import { useSigningContext } from '@domain/signing';
import { CreditCard, CreditCardAccount, TransferMoneyRequest } from '@generated-types';

import { TransferFormFields } from './TransferFormFields';
import { KbTransferForm, TransferSchema } from './TransferSchema';

import './TransferForm.scss';

type PaymentFormProps = {
    card: CreditCard;
    account: CreditCardAccount;
    setOpen(state: boolean): void;
};

export const TransferForm = ({ card, account, setOpen }: PaymentFormProps) => {
    const { status, error: signingError } = useSigningContext();
    const accountBalance = AccountService.getBalance(account);
    const { toggles } = useSession();

    const text = useText();
    const formContext = useForm<KbTransferForm>({
        mode: 'onBlur',
        resolver: zodResolver(TransferSchema(toggles.KREDITTBANKEN_PAY)),
        defaultValues: {
            account: {
                number: '',
                ownership: false,
            },
            maxAmount: accountBalance.balance,
        },
    });

    const transferMutation = useTransferToAccountMutation();

    const onSubmit: SubmitHandler<KbTransferForm> = ({ account: payeeAccountNumber, amount }) => {
        const transferParameters: TransferMoneyRequest = {
            amount,
            recipientAccountNumber: payeeAccountNumber.number,
            senderAccountId: account.accountId,
            senderCardId: card.id,
        };

        transferMutation.mutate(transferParameters);
    };

    const onCloseModal = () => {
        transferMutation.reset();
        setOpen(false);
    };

    const showBankID = transferMutation.data;

    if (transferMutation.isError) {
        return (
            <ErrorCard
                isModal
                title={text.messageErrorUps}
                description={text.transferDeclinedText}
                icon={IconTypes.WALLET_QUESTIONMARK}
            />
        );
    }

    if (status) {
        return (
            <TransferResult
                status={status}
                redirectTransactions={onCloseModal}
                text={text}
                paymentError={signingError}
                type="transfer"
            />
        );
    }

    return (
        <FormProvider {...formContext}>
            <form className="transfer-form" onSubmit={formContext.handleSubmit(onSubmit)}>
                <TransferFormFields card={card} account={account} />
                <ButtonWithSpinner
                    className="transfer-form__submit"
                    buttonType={ButtonTypes.STANDARD_POSITIVE}
                    type="submit"
                    showSpinner={transferMutation.isPending}
                    data-track-id={piwik.transfer.transferSubmit.dataTrackId}
                    data-track-linkname={piwik.transfer.transferSubmit.dataTrackLinkname}
                    id={piwik.transfer.transferSubmit.dataTrackId}
                >
                    {text.transferSubmit}
                </ButtonWithSpinner>
                {showBankID && (
                    <BankIdModal
                        appNamespace="view-extra-payment-wrapper"
                        dynamicLinkId={transferMutation.data.dynamicLinkId.value}
                        id="extra-payment"
                        setOpen={onCloseModal}
                        redirectUri={transferMutation.data.redirectUrl}
                        open
                        show
                    />
                )}
            </form>
        </FormProvider>
    );
};
